import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import type { FirebaseError } from "firebase/app";
import { Loader2 } from "lucide-react";
import { auth } from "@/lib/firebase";
import { exchangeFirebaseToken } from "@/features/auth/lib/exchange";
import {
  ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL,
  POPUP_PROVIDER_COPY,
  POPUP_PROVIDERS,
  isPopupDismissed,
  type PopupProviderId,
} from "@/features/auth/lib/popup-sign-in";
import { ProviderSignInButton } from "@/features/auth/components/ProviderSignInButton";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getDefaultRoute } from "@/features/auth/lib/page-routes";
import { toast } from "sonner";
import { Sentry } from "@/lib/sentry";
import {
  AdgeLogo,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PasswordInput,
} from "@nexus/ui";

const NOT_PROVISIONED_MARKER = "User not found";

interface LoginFormProps {
  /** The previous session ended and could not be refreshed. */
  isSessionExpired?: boolean;
}

export function LoginForm({ isSessionExpired = false }: LoginFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lastVisitedRoute = useAuth((s) => s.lastVisitedRoute);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [popupLoading, setPopupLoading] = useState<PopupProviderId | null>(null);

  async function handleExchangeAndNavigate(
    firebaseToken: string,
    extra?: { picture?: string | null; name?: string | null },
  ) {
    let defaultRoute: string | null;
    try {
      const { tenants } = await exchangeFirebaseToken(firebaseToken, extra);
      defaultRoute = getDefaultRoute(tenants, lastVisitedRoute);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes(NOT_PROVISIONED_MARKER)) {
        toast.error(t("auth.accountNotProvisioned"));
      } else {
        Sentry.captureException(err, { tags: { flow: "auth-exchange" } });
        toast.error(t("auth.exchangeError"));
      }
      throw err;
    }
    // Fire navigation without awaiting — the router transitions immediately
    // and the destination route's `pendingComponent` (e.g. the dashboard
    // skeleton) renders inside the AppShell while its loader runs. Awaiting
    // here would keep the LoginForm mounted with its spinner for the full
    // duration of the loader, which is the bug we're fixing.
    void navigate({ to: defaultRoute ?? "/login" });
  }

  async function handlePasswordLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || !password) return;
    setFieldError(null);
    setLoading(true);

    try {
      let credential;
      try {
        credential = await signInWithEmailAndPassword(auth, email, password);
      } catch {
        setFieldError(t("auth.loginError"));
        return;
      }

      const firebaseToken = await credential.user.getIdToken(true);
      await handleExchangeAndNavigate(firebaseToken);
    } catch {
      // Exchange errors already toasted in handleExchangeAndNavigate
    } finally {
      setLoading(false);
    }
  }

  async function handlePopupLogin(provider: PopupProviderId) {
    setPopupLoading(provider);
    try {
      let credential;
      try {
        credential = await signInWithPopup(auth, POPUP_PROVIDERS[provider]);
      } catch (err) {
        const firebaseErr = err as FirebaseError;
        const code = firebaseErr.code;
        if (isPopupDismissed(code)) return;
        if (code === ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL) {
          toast.error(t("auth.accountExistsWithDifferentProvider"));
          return;
        }
        console.error(`[${provider} sign-in]`, code, firebaseErr.message);
        Sentry.captureException(err, { tags: { flow: `${provider}-signin`, errorCode: code } });
        toast.error(t(POPUP_PROVIDER_COPY[provider].error));
        return;
      }

      const firebaseToken = await credential.user.getIdToken(true);
      await handleExchangeAndNavigate(firebaseToken, {
        picture: credential.user.photoURL,
        name: credential.user.displayName,
      });
    } catch {
      // Exchange errors already toasted
    } finally {
      setPopupLoading(null);
    }
  }

  const anyLoading = loading || popupLoading !== null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--background)] p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1 text-start">
          <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
          <CardTitle className="text-xl font-normal">{t("auth.title")}</CardTitle>
          <CardDescription className="text-sm">{t("auth.subtitle")}</CardDescription>
          {isSessionExpired && (
            <p role="status" className="text-sm text-[var(--muted-foreground)]">
              {t("auth.sessionExpired")}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handlePasswordLogin} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("auth.emailPlaceholder", "you@company.com")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldError) setFieldError(null);
                }}
                required
                autoComplete="email"
                disabled={anyLoading}
                aria-invalid={fieldError ? true : undefined}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Link
                  to="/auth/forgot-password"
                  className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] underline-offset-4 hover:underline"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldError) setFieldError(null);
                }}
                required
                autoComplete="current-password"
                disabled={anyLoading}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? "login-error" : undefined}
                showPasswordLabel={t("auth.showPassword")}
                hidePasswordLabel={t("auth.hidePassword")}
              />
              {fieldError && (
                <p id="login-error" role="alert" className="text-xs text-[var(--destructive)]">
                  {fieldError}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full gap-2" disabled={anyLoading} aria-busy={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              <span>{loading ? t("auth.signingIn") : t("auth.signIn")}</span>
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-[var(--card)] px-2 text-[var(--muted-foreground)]">
                {t("auth.orDivider")}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <ProviderSignInButton
              provider="google"
              isLoading={popupLoading === "google"}
              disabled={anyLoading}
              onClick={() => void handlePopupLogin("google")}
            />
            <ProviderSignInButton
              provider="microsoft"
              isLoading={popupLoading === "microsoft"}
              disabled={anyLoading}
              onClick={() => void handlePopupLogin("microsoft")}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
